/**
 * Postcard SSE Notifications Hook
 *
 * Listens for Server-Sent Events from the backend when postcards are
 * uploaded via Gmail polling. Creates notifications in the notification
 * store so they appear on the dashboard.
 *
 * Usage:
 * - Call this hook once at the app level (e.g., in App.tsx or a layout)
 * - It will automatically subscribe to the "postcards" SSE channel
 * - When a postcard is created, it adds a notification to the store
 */

import { useEffect, useRef, useState } from 'react';
import { useNotificationActions } from '../store/notificationStore';

interface PostcardSSEEventData {
  event: string;
  channel: string;
  resource_id: number;
  data: {
    filename: string;
    event_name?: string;
    event_type?: string;
    source?: string;
  };
  timestamp: string;
}

/**
 * Get JWT token from auth storage
 */
function getAuthToken(): string | null {
  // Try Zustand persist storage first (dms-auth-storage)
  const authData = localStorage.getItem('dms-auth-storage');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      const token = parsed.state?.accessToken;
      if (token) return token;
    } catch (error) {
      console.error('[PostcardSSE] Failed to parse auth storage:', error);
    }
  }

  // Fallback to sessionStorage for non-persistent sessions
  return sessionStorage.getItem('access_token');
}

/**
 * Get API base URL for SSE connection
 */
function getApiBaseUrl(): string {
  // Check for manual override (mobile/ngrok testing)
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('api_base_url_override');
    if (override) return override;
  }

  // Production: use environment variable
  // In dev, empty string lets Vite proxy handle routing
  return import.meta.env.VITE_API_BASE_URL || '';
}

/**
 * Hook to listen for postcard SSE events and create notifications
 *
 * @param enabled - Whether to enable SSE listening (default: true)
 */
export function usePostcardSSENotifications(enabled = true) {
  const { addNotification } = useNotificationActions();
  const eventSourceRef = useRef<EventSource | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const connect = () => {
      // Clear any existing reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Get authentication token
      const token = getAuthToken();
      if (!token) {
        console.warn('[PostcardSSE] No authentication token found - skipping SSE connection');
        setIsConnected(false);
        return;
      }

      // Build SSE URL with token (EventSource cannot send headers)
      const baseUrl = getApiBaseUrl();
      const sseUrl = `${baseUrl}/api/sse/subscribe?channels=postcards&token=${encodeURIComponent(token)}`;

      console.log('[PostcardSSE] Connecting to SSE endpoint...');

      // Close existing connection if any
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Create new EventSource connection
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('[PostcardSSE] Connected to postcards channel');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event: MessageEvent) => {
        try {
          const eventData = JSON.parse(event.data) as PostcardSSEEventData;

          // Only handle postcard created events
          if (eventData.channel === 'postcards' && eventData.event === 'created') {
            const filename = eventData.data?.filename || 'Unknown';
            const eventName = eventData.data?.event_name;

            // Create notification
            addNotification({
              type: 'postcard_uploaded',
              title: 'New Postcard from Gmail',
              message: eventName
                ? `"${filename}" for ${eventName} has been processed`
                : `"${filename}" has been processed and is ready for use`,
              severity: 'success',
              actionUrl: '/usi-events',
              actionLabel: 'View Postcards',
              metadata: {
                postcard_id: eventData.resource_id,
                filename: filename,
                event_name: eventName,
                source: 'gmail_polling',
              },
            });

            console.log('[PostcardSSE] Notification created for postcard:', eventData.resource_id);
          }
        } catch (error) {
          console.error('[PostcardSSE] Failed to parse message:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.warn('[PostcardSSE] Connection error:', error);
        setIsConnected(false);

        // Close the connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Attempt reconnection if under max attempts
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(5000 * reconnectAttemptsRef.current, 30000);
          console.log(
            `[PostcardSSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
          );
          reconnectTimerRef.current = window.setTimeout(connect, delay);
        } else {
          console.warn('[PostcardSSE] Max reconnect attempts reached, giving up');
        }
      };
    };

    // Initial connection
    connect();

    // Cleanup on unmount
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [enabled, addNotification]);

  return {
    isConnected,
  };
}

export default usePostcardSSENotifications;
